import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { ShopProduct } from './data/shop';
export interface ShopLine extends ShopProduct {
  quantity: number;
}
interface ShopContextType {
  lines: ShopLine[];
  add: (product: ShopProduct) => void;
  setQuantity: (id: string, quantity: number) => void;
  remove: (id: string) => void;
  clear: () => void;
  count: number;
  total: number;
}
const STORAGE_KEY = 'smaken-shop-v1';
const ShopContext = createContext<ShopContextType | undefined>(undefined);
export function ShopProvider({
  children
}: {
  children: ReactNode;
}) {
  const [lines, setLines] = useState<ShopLine[]>([]);
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) setLines(JSON.parse(raw));
    } catch {
      /* ignore unreadable storage */
    }
  }, []);
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
    } catch {
      /* ignore write failures */
    }
  }, [lines]);
  const add = (product: ShopProduct) => setLines(prev => {
    const existing = prev.find(l => l.id === product.id);
    if (existing) {
      return prev.map(l => l.id === product.id ? {
        ...l,
        quantity: l.quantity + 1
      } : l);
    }
    return [...prev, {
      ...product,
      quantity: 1
    }];
  });
  const setQuantity = (id: string, quantity: number) => setLines(prev => quantity < 1 ? prev.filter(l => l.id !== id) : prev.map(l => l.id === id ? {
    ...l,
    quantity
  } : l));
  const remove = (id: string) => setLines(prev => prev.filter(l => l.id !== id));
  const clear = () => setLines([]);
  const count = lines.reduce((sum, l) => sum + l.quantity, 0);
  const total = lines.reduce((sum, l) => sum + l.price * l.quantity, 0);
  return <ShopContext.Provider value={{
    lines,
    add,
    setQuantity,
    remove,
    clear,
    count,
    total
  }} data-id="element-1401" data-mp="1jtceuoy6nqoe">
      {children}
    </ShopContext.Provider>;
}
export function useShop() {
  const context = useContext(ShopContext);
  if (context === undefined) {
    throw new Error('useShop must be used within a ShopProvider');
  }
  return context;
}